import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, CheckCircle } from 'lucide-react';

const PRODUCTS = ['Golden Morning', 'River Bloom', 'Wild Forest', 'The Complete Collection'];
const STATES = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

export default function ReviewForm() {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [name, setName] = useState('');
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('');
  const [product, setProduct] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) { setError('Please select a star rating.'); return; }
    if (!name.trim() || !text.trim()) { setError('Please enter your name and review.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), rating, text: text.trim(), product: product || null, suburb: suburb.trim() || null, state: state || null })
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || 'Something went wrong. Please try again.');
      } else {
        setSubmitted(true);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  const inputCls = "w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm";

  if (submitted) {
    return (
      <div className="min-h-screen pt-56 pb-24">
        <div className="max-w-xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} data-testid="review-thank-you">
            <CheckCircle className="w-16 h-16 text-primary mx-auto mb-6" />
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Thank you!
            </h1>
            <p className="text-foreground/70">
              Your review has been received. Once it's checked by our team it will appear on the website. We really appreciate you taking the time. 🍯
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-56 pb-24">
      <div className="max-w-xl mx-auto px-4 md:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 text-center" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="review-heading">
            Leave a Review
          </h1>
          <p className="text-foreground/70 text-center mb-10">
            Tried our honey? We'd love to hear what you think.
          </p>

          <form onSubmit={handleSubmit} className="p-8 rounded-2xl border border-border bg-muted/30 space-y-5" data-testid="review-form">
            <div className="text-center">
              <label className="block text-sm font-medium text-foreground mb-3">Your Rating</label>
              <div className="flex justify-center space-x-2" data-testid="star-rating">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button"
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHoverRating(n)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="transition-transform hover:scale-110"
                    data-testid={`star-${n}`}>
                    <Star className={`w-9 h-9 ${(hoverRating || rating) >= n ? 'fill-amber-400 text-amber-400' : 'text-border'}`} />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Your Name *</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Sarah M." className={inputCls} data-testid="review-name-input" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Suburb</label>
                <input value={suburb} onChange={e => setSuburb(e.target.value)} placeholder="e.g. Bondi" className={inputCls} data-testid="review-suburb-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">State</label>
                <select value={state} onChange={e => setState(e.target.value)} className={inputCls} data-testid="review-state-select">
                  <option value="">Select...</option>
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Which honey did you try?</label>
              <select value={product} onChange={e => setProduct(e.target.value)} className={inputCls} data-testid="review-product-select">
                <option value="">Select... (optional)</option>
                {PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Your Review *</label>
              <textarea value={text} onChange={e => setText(e.target.value)} rows={4} placeholder="Tell us what you loved..." className={inputCls} data-testid="review-text-input" />
            </div>

            <button type="submit" disabled={submitting}
              className="w-full px-8 py-4 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-all duration-300 font-semibold text-lg disabled:opacity-50"
              data-testid="review-submit-button">
              {submitting ? 'Sending...' : 'Submit Review'}
            </button>
            {error && <p className="text-red-500 text-sm text-center" data-testid="review-error">{error}</p>}
          </form>
        </motion.div>
      </div>
    </div>
  );
}
